import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface AIRequestOptions {
  functionName: string;
  body?: Record<string, unknown>;
  onSuccess?: (data: unknown) => void;
  onError?: (error: Error) => void;
}

interface AIRequestResult<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
}

interface PlanError {
  error: string;
  message: string;
  upgrade_required?: boolean;
  plan?: string;
  remaining?: number;
  resetInMinutes?: number;
}

export const handleAIError = (error: unknown, navigate?: (path: string) => void) => {
  const err = error as { message?: string; status?: number; body?: PlanError };
  
  // Check for plan access denied errors
  if (err?.body?.error === 'PLAN_ACCESS_DENIED' || err?.body?.upgrade_required) {
    toast.error('🔒 Funcionalidade Premium', {
      description: err.body?.message || 'Esta ferramenta de IA está disponível apenas no plano Profissional.',
      duration: 8000,
      action: {
        label: 'Fazer Upgrade',
        onClick: () => {
          if (navigate) {
            navigate('/assinatura');
          } else {
            window.location.href = '/assinatura';
          }
        },
      },
    });
    return true;
  }

  // Check for rate limit errors
  if (err?.body?.error === 'RATE_LIMIT_EXCEEDED') {
    toast.warning('⏱️ Limite de uso atingido', {
      description: `Você atingiu o limite de requisições. Tente novamente em ${err.body?.resetInMinutes || 60} minutos.`,
      duration: 6000,
    });
    return true;
  }

  // Check for authentication errors
  if (err?.body?.error === 'AUTH_REQUIRED' || err?.status === 401) {
    toast.error('Sessão expirada', {
      description: 'Por favor, faça login novamente.',
      action: {
        label: 'Login',
        onClick: () => {
          if (navigate) {
            navigate('/');
          } else {
            window.location.href = '/';
          }
        },
      },
    });
    return true;
  }

  return false;
};

export const useAIRequest = <T = unknown>() => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<T | null>(null);
  const navigate = useNavigate();

  const executeRequest = useCallback(async ({
    functionName,
    body = {},
    onSuccess,
    onError,
  }: AIRequestOptions): Promise<AIRequestResult<T>> => {
    setLoading(true);
    setError(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw { 
          message: 'Not authenticated', 
          status: 401,
          body: { error: 'AUTH_REQUIRED', message: 'Por favor, faça login.' }
        };
      }

      const { data: responseData, error: responseError } = await supabase.functions.invoke(
        functionName,
        {
          body,
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
          },
        }
      );

      if (responseError) {
        // Try to parse error body
        let errorBody: PlanError | undefined;
        try {
          if (typeof responseError.message === 'string') {
            errorBody = JSON.parse(responseError.message);
          }
        } catch {
          // Not JSON, use raw message
        }

        const customError = {
          message: responseError.message,
          status: responseError.context?.status || 500,
          body: errorBody || responseData,
        };

        // Handle AI-specific errors with upsell
        if (handleAIError(customError, navigate)) {
          const err = new Error(responseError.message);
          setError(err);
          onError?.(err);
          return { data: null, error: err, loading: false };
        }

        throw customError;
      }

      // Check if response itself contains an error
      if (responseData?.error) {
        const customError = {
          message: responseData.error,
          status: 403,
          body: responseData,
        };

        if (handleAIError(customError, navigate)) {
          const err = new Error(responseData.error);
          setError(err);
          onError?.(err);
          return { data: null, error: err, loading: false };
        }
      }

      setData(responseData as T);
      onSuccess?.(responseData);
      return { data: responseData as T, error: null, loading: false };

    } catch (err: unknown) {
      const error = err as Error & { status?: number; body?: PlanError };
      
      // Try to handle as AI error first
      if (!handleAIError(err, navigate)) {
        // Generic error fallback
        toast.error('Erro ao processar', {
          description: error.message || 'Ocorreu um erro inesperado. Tente novamente.',
        });
      }

      setError(error);
      return { data: null, error, loading: false };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  return {
    executeRequest,
    loading,
    error,
    data,
  };
};

// Utility function for quick AI requests
export const invokeAIFunction = async <T = unknown>(
  functionName: string,
  body: Record<string, unknown> = {},
  navigate?: (path: string) => void
): Promise<{ data: T | null; error: Error | null }> => {
  try {
    const { data: session } = await supabase.auth.getSession();
    
    if (!session?.session) {
      handleAIError({ status: 401, body: { error: 'AUTH_REQUIRED' } }, navigate);
      return { data: null, error: new Error('Not authenticated') };
    }

    const { data, error } = await supabase.functions.invoke(functionName, {
      body,
      headers: {
        Authorization: `Bearer ${session.session.access_token}`,
      },
    });

    if (error) {
      const customError = { message: error.message, body: data };
      handleAIError(customError, navigate);
      return { data: null, error };
    }

    if (data?.error) {
      handleAIError({ body: data }, navigate);
      return { data: null, error: new Error(data.error) };
    }

    return { data: data as T, error: null };
  } catch (err) {
    const error = err as Error;
    handleAIError(err, navigate);
    return { data: null, error };
  }
};
